import React, { useState, useEffect } from 'react';
import { Users, Loader2, CheckCircle, XCircle, AlertCircle, LogIn } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TeamInvite = ({ onClose, onNeedLogin }) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptedTeam, setAcceptedTeam] = useState(null);

  // Get token from URL
  const token = new URLSearchParams(window.location.search).get('token');

  useEffect(() => {
    if (token) {
      verifyInvitation();
    } else {
      setError('No invitation token provided');
      setIsLoading(false);
    }
  }, [token]);

  const verifyInvitation = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/invite/verify?token=${token}`);
      const data = await response.json();
      
      if (!data.valid) {
        setError(data.error || 'Invalid invitation');
      } else {
        setInvitation(data);
      }
    } catch (err) {
      setError('Failed to verify invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!user?.uid || !token) return;
    
    setIsAccepting(true);
    setError(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/teams/invite/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          user_id: user.uid,
          user_name: user.displayName || user.email
        })
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to accept invitation');
      
      setAccepted(true);
      setAcceptedTeam(data.team);
      
      // Clear URL params
      window.history.replaceState({}, '', window.location.pathname);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-indigo-400 mx-auto mb-4" />
          <p className="text-slate-400">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Invalid Invitation</h2>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Welcome to the Team!</h2>
          <p className="text-slate-400 mb-6">
            You've successfully joined <span className="text-indigo-400 font-semibold">{acceptedTeam?.name}</span>
          </p>
          <button
            onClick={onClose}
            className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-xl transition-colors"
          >
            Get Started
          </button>
        </div>
      </div>
    );
  }

  // Need to login first
  if (!user) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-indigo-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Team Invitation</h2>
          <p className="text-slate-400 mb-2">
            <span className="text-white font-semibold">{invitation?.invited_by}</span> has invited you to join
          </p>
          <p className="text-xl text-indigo-400 font-bold mb-6">{invitation?.team_name}</p>
          
          <div className="bg-amber-900/30 border border-amber-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-amber-400">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">Please log in or sign up to accept this invitation</p>
            </div>
          </div>
          
          <button
            onClick={onNeedLogin}
            className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors"
          >
            <LogIn className="w-5 h-5" />
            Log In to Accept
          </button>
        </div>
      </div>
    );
  }

  // Email mismatch warning
  const emailMismatch = invitation?.email && user?.email?.toLowerCase() !== invitation.email.toLowerCase();

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center max-w-md">
        <div className="w-16 h-16 bg-indigo-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <Users className="w-8 h-8 text-indigo-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-3">Team Invitation</h2>
        <p className="text-slate-400 mb-2">
          <span className="text-white font-semibold">{invitation?.invited_by}</span> has invited you to join
        </p>
        <p className="text-xl text-indigo-400 font-bold mb-6">{invitation?.team_name}</p>
        
        {emailMismatch && (
          <div className="bg-amber-900/30 border border-amber-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-amber-400 text-sm">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <p>
                This invitation was sent to <span className="font-semibold">{invitation.email}</span> but you're logged in as <span className="font-semibold">{user.email}</span>
              </p>
            </div>
          </div>
        )}
        
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-colors"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            disabled={isAccepting}
            className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 transition-colors"
          >
            {isAccepting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Accept
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeamInvite;
